'use client';

import { create } from 'zustand';
import type { AuthUser } from '@alqove/types';
import { api } from '@/lib/api';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;

  setAuth: (user: AuthUser, token: string) => void;
  clearAuth: () => void;
  loadFromStorage: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: null,
  isLoading: true,

  setAuth: (user, token) => {
    localStorage.setItem('auth_token', token);
    set({ user, token, isLoading: false });
  },

  clearAuth: () => {
    localStorage.removeItem('auth_token');
    set({ user: null, token: null, isLoading: false });
  },

  loadFromStorage: async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      set({ isLoading: false });
      return;
    }

    try {
      const response = await api.auth.me();
      set({ user: response.data, token, isLoading: false });
    } catch {
      localStorage.removeItem('auth_token');
      set({ user: null, token: null, isLoading: false });
    }
  },

  login: async (email, password) => {
    const response = await api.auth.login({ email, password });
    const { user, token } = response.data;
    localStorage.setItem('auth_token', token);
    set({ user, token, isLoading: false });
  },

  register: async (name, email, password) => {
    const response = await api.auth.register({
      name,
      email,
      password,
      password_confirmation: password,
    });
    const { user, token } = response.data;
    localStorage.setItem('auth_token', token);
    set({ user, token, isLoading: false });
  },

  logout: async () => {
    try {
      await api.auth.logout();
    } catch {
      // Token may already be invalid — clear anyway
    }
    localStorage.removeItem('auth_token');
    set({ user: null, token: null, isLoading: false });
  },
}));
